import { ReactElement, ReactNode } from 'react';
import { TabsProps } from 'antd';
import { useTranslation } from 'react-i18next';
import { Outlet, matchPath, useLocation, useNavigate, useLoaderData } from 'react-router-dom';
import { queryKeysClient } from 'common/clients/query-key.client';
import { queryClient } from 'common/clients/query.client';
import { Button } from 'common/components/antd/Button/Button';
import { Tabs } from 'common/components/antd/Tabs/Tabs';
import { ENV_COUNTERPARTY_GUID } from 'common/constants/env.constants';
import { IS_ADMIN_FE } from 'common/constants/general-access-role.constants';
import { EQueryKeys } from 'common/enums/query-kyes.enum';
import { TScopeResData } from 'domains/roles/types/roles.types';
import { HeaderComponent } from 'pages/user-role-management/layout/components/Header/Header.component';
import { ROUTES_MAPPING } from 'navigation/constants/route.constants';
import { STitle, SHeader, SContainer, SContent, STabs } from './user-role-management.layout.style';
import {
  EUserManagementBranchRolesPageGetParams,
  EUserManagementBranchRolesPageGetParamsActionTypes,
} from '../branch-roles/enums/branch-roles.enum';
import {
  EUserManagementBranchesPageGetParams,
  EUserManagementBranchesPageGetParamsActionTypes,
} from '../branches/enums/branches.enum';
import {
  EUserManagementCounterpartiesPageGetParams,
  EUserManagementCounterpartiesPageGetParamsActionTypes,
} from '../counterparties/enums/conterparties.enums';
import {
  EUserManagementCustomersPageGetParams,
  EUserManagementCustomersPageGetParamsActionTypes,
} from '../customers/enums/customer.enum';
import {
	EUserManagementMerchantsPageGetParams,
	EUserManagementMerchantsPageGetParamsActionTypes,
} from '../merchants/enums/merchants.enums';
import {
  EUserManagementPermissionsPageGetParams,
  EUserManagementPermissionsPageGetParamsActionTypes
} from '../permissions/enums/permissions.enums';
import {
  EUserManagementRolesPageGetParams,
  EUserManagementRolesPageGetParamsActionTypes,
} from '../roles/enums/roles.enum';

type TTab = {
  path: string;
  key: string;
  label: ReactNode;
  extraContent: ReactNode;
};

type TUserManagementPageLoaderData = {
  hasAccessForAllBranches: boolean;
}

enum ERoleSectionKey {
  Branches = 'branches',
  Counterparties = 'counterparties',
  Employees = 'employees',
  Roles = 'roles',
  BranchRoles = 'branch-roles',
  Customers = 'customers',
  Merchants = 'merchants',
  KybKyc = 'kyb-kyc',
  Permissions = 'permissions',
}

const UserManagementLayout = (): ReactElement => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const loaderData = useLoaderData() as TUserManagementPageLoaderData;

  const tabs: TTab[] = [
    ...(IS_ADMIN_FE
      ? [
          {
            key: ERoleSectionKey.Counterparties,
            label: t('usrRoleMng.tabs.counterparties.title'),
            path: ROUTES_MAPPING.PROTECTED.USER_ROLE_MANAGEMENT_COUNTERPARTIES,
            extraContent: (
              <div
                style={{
                  marginRight: 16,
                }}
              >
                <Button
                  onClick={(): void => {
                    const url = ROUTES_MAPPING.PROTECTED.USER_ROLE_MANAGEMENT_COUNTERPARTIES;
                    const actionGetParam = EUserManagementCounterpartiesPageGetParams.Action;
                    const actionGetParamValue = EUserManagementCounterpartiesPageGetParamsActionTypes.Create;

                    navigate(`${url}?${actionGetParam}=${actionGetParamValue}`);
                  }}
                  type="primary"
                >
                  {t('usrRoleMng.counterparties.button.add')}
                </Button>
              </div>
            ),
          },
        ]
      : []),
    {
      key: ERoleSectionKey.Branches,
      label: t('usrRoleMng.tabs.branches.title'),
      path: ROUTES_MAPPING.PROTECTED.USER_ROLE_MANAGEMENT_BRANCHES,
      extraContent: (
        <div
          style={{
            marginRight: 16,
          }}
        >
          <Button
            onClick={(): void => {
              const url = ROUTES_MAPPING.PROTECTED.USER_ROLE_MANAGEMENT_BRANCHES;
              const actionGetParam = EUserManagementBranchesPageGetParams.Action;
              const actionGetParamValue = EUserManagementBranchesPageGetParamsActionTypes.Create;

              navigate(`${url}?${actionGetParam}=${actionGetParamValue}`);
            }}
            type="primary"
          >
            {t('usrRoleMng.branches.button.add')}
          </Button>
        </div>
      ),
    },
    {
      key: ERoleSectionKey.Employees,
      label: t('usrRoleMng.tabs.employees.title'),
      path: ROUTES_MAPPING.PROTECTED.USER_ROLE_MANAGEMENT_EMPLOYEES,
      extraContent: '',
    },
    ...(loaderData?.hasAccessForAllBranches
      ? [
          {
            key: ERoleSectionKey.Roles,
            label: t('usrRoleMng.tabs.roles.title'),
            path: ROUTES_MAPPING.PROTECTED.USER_ROLE_MANAGEMENT_ROLES,
            extraContent: (
              <div
                style={{
                  marginRight: 16,
                }}
              >
                <Button
                  onClick={(): void => {
                    const url = ROUTES_MAPPING.PROTECTED.USER_ROLE_MANAGEMENT_ROLES;
                    const actionGetParam = EUserManagementRolesPageGetParams.Action;
                    const actionGetParamValue = EUserManagementRolesPageGetParamsActionTypes.Create;

                    navigate(`${url}?${actionGetParam}=${actionGetParamValue}`);
                  }}
                  type="primary"
                >
                  {t('usrRoleMng.roles.button.add')}
                </Button>
              </div>
            ),
          },
        ]
      : []),
    {
      key: ERoleSectionKey.BranchRoles,
      label: t('usrRoleMng.tabs.branchRoles.title'),
      path: ROUTES_MAPPING.PROTECTED.USER_ROLE_MANAGEMENT_BRANCH_ROLES,
      extraContent: (
        <div
          style={{
            marginRight: 16,
          }}
        >
          <Button
            onClick={(): void => {
              const url = ROUTES_MAPPING.PROTECTED.USER_ROLE_MANAGEMENT_BRANCH_ROLES;
              const actionGetParam = EUserManagementBranchRolesPageGetParams.Action;
              const actionGetParamValue = EUserManagementBranchRolesPageGetParamsActionTypes.Create;

              navigate(`${url}?${actionGetParam}=${actionGetParamValue}`);
            }}
            type="primary"
          >
            {t('usrRoleMng.branchRoles.button.add')}
          </Button>
        </div>
      ),
    },
    {
      key: ERoleSectionKey.Customers,
      label: t('usrRoleMng.tabs.customers.title'),
      path: ROUTES_MAPPING.PROTECTED.USER_ROLE_MANAGEMENT_CUSTOMERS,
      extraContent: (
        <div
          style={{
            marginRight: 16,
          }}
        >
          {!IS_ADMIN_FE && (
            <Button
              onClick={(): void => {
                const url = ROUTES_MAPPING.PROTECTED.USER_ROLE_MANAGEMENT_CUSTOMERS;
                const actionGetParam = EUserManagementCustomersPageGetParams.Action;
                const actionGetParamValue = EUserManagementCustomersPageGetParamsActionTypes.Create;

                navigate(`${url}?${actionGetParam}=${actionGetParamValue}`);
              }}
              type="primary"
            >
              {t('usrRoleMng.customers.button.add')}
            </Button>
          )}
        </div>
      ),
    },
	  ...(IS_ADMIN_FE
		  ? [
				  {
			      key: ERoleSectionKey.Merchants,
			      label: t('usrRoleMng.tabs.merchants.title'),
			      path: ROUTES_MAPPING.PROTECTED.USER_ROLE_MANAGEMENT_MERCHANTS,
			      extraContent: (
			        <div style={{ marginRight: 16 }}>
		            <Button
		              onClick={(): void => {
		                const url = ROUTES_MAPPING.PROTECTED.USER_ROLE_MANAGEMENT_MERCHANTS;
		                const actionGetParam = EUserManagementMerchantsPageGetParams.Action;
		                const actionGetParamValue = EUserManagementMerchantsPageGetParamsActionTypes.Create;

		                navigate(`${url}?${actionGetParam}=${actionGetParamValue}`);
		              }}
		              type="primary"
		            >
		              {t('usrRoleMng.merchants.button.add')}
		            </Button>
			        </div>
			      ),
			    },
          {
            key: ERoleSectionKey.Permissions,
            label: t('usrRoleMng.tabs.permissions.title'),
            path: ROUTES_MAPPING.PROTECTED.USER_ROLE_MANAGEMENT_PERMISSIONS,
            extraContent: (
              <div style={{ marginRight: 16 }}>
                <Button
                  onClick={(): void => {
                    const url = ROUTES_MAPPING.PROTECTED.USER_ROLE_MANAGEMENT_PERMISSIONS;
                    const actionGetParam = EUserManagementPermissionsPageGetParams.Action;
                    const actionGetParamValue = EUserManagementPermissionsPageGetParamsActionTypes.Create;

                    navigate(`${url}?${actionGetParam}=${actionGetParamValue}`);
                  }}
                  type="primary"
                >
                  {t('usrRoleMng.permissions.button.add')}
                </Button>
              </div>
            ),
          },
		  ]
		  : []),
    // {
    //   key: ERoleSectionKey.KybKyc,
    //   label: t('usrRoleMng.tabs.kycKyb'),
    //   path: ROUTES_MAPPING.PROTECTED.USER_ROLE_MANAGEMENT_KYC,
    //   extraContent: null,
    // },
  ];

  const activeTab = tabs.find((tab) => {
    const match = matchPath(
      {
        path: tab.path,
        end: false,
        caseSensitive: false,
      },
      location.pathname,
    );

    return !!match;
  });

  const extraContent = {
    right: activeTab?.extraContent,
    left: <div style={{ width: 24 }} />,
  };

  const onChangeTab: TabsProps['onChange'] = (key) => {
    const tabChangeTo = tabs.find((tab) => tab.key === key);
    if (tabChangeTo) {
      navigate(tabChangeTo.path);
    }
  };

  return (
    <SContainer>
      <STitle>{t('usrRoleMng.title')}</STitle>
      <SHeader>
        <HeaderComponent />
      </SHeader>
      <SContent>
        <STabs>
          <Tabs onChange={onChangeTab} activeKey={activeTab?.key} tabBarExtraContent={extraContent} items={tabs} />
          <Outlet />
        </STabs>
      </SContent>
    </SContainer>
  );
};

export const loader = async (): Promise<{hasAccessForAllBranches: boolean}> => {
  try {
    const scopeListDataQuery =
      queryKeysClient[EQueryKeys.RbacQueryKeys].getCounterpartyScopesList(ENV_COUNTERPARTY_GUID);

    const scopeListData: TScopeResData =
      queryClient.getQueryData(scopeListDataQuery.queryKey) ??
      (await queryClient.fetchQuery(scopeListDataQuery));

    const hasAccessForAllBranches =
      ((scopeListData as TScopeResData).scopes.find(({ scope }) => scope === 'branches')?.is_allowed) ||
      false;

    return {
      hasAccessForAllBranches,
    };
  } catch (error) {
    console.error(error);

    return {
      hasAccessForAllBranches: false,
    };
  }
};

export default UserManagementLayout;
